import React, { FormEvent, useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Material } from '../types';
import * as quotesService from '../services/quotes.service';
import * as materialsService from '../services/materials.service';
import { usePhoneMask } from '../hooks/usePhoneMask';
import { useDocumentMask } from '../hooks/useDocumentMask';
import { getMaxDimensions } from '../utils/dimension-validator';

interface QuoteItemForm {
  id: string;
  materialId: string;
  width: number;
  height: number;
  thickness: number;
  quantity: number;
  color?: string;
  unitPrice: number;
  subtotal: number;
}

interface CustomItemForm {
  id: string;
  description: string;
  value: number;
}

interface CustomerForm {
  name: string;
  company?: string;
  document: string;
  email: string;
  phone: string;
}

interface CreateQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  accessToken?: string;
}

export function CreateQuoteDialog({
  open,
  onOpenChange,
  onSuccess,
  accessToken
}: CreateQuoteDialogProps) {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Input masks
  const phoneMask = usePhoneMask();
  const documentMask = useDocumentMask();

  // Customer form state
  const [customerName, setCustomerName] = useState('');
  const [customerCompany, setCustomerCompany] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Items state
  const [items, setItems] = useState<QuoteItemForm[]>([]);

  // Custom items state
  const [customItems, setCustomItems] = useState<CustomItemForm[]>([]);
  const [currentCustomDescription, setCurrentCustomDescription] = useState('');
  const [currentCustomValue, setCurrentCustomValue] = useState('');

  // Current item being added/edited
  const [currentMaterialId, setCurrentMaterialId] = useState('');
  const [currentWidth, setCurrentWidth] = useState('');
  const [currentHeight, setCurrentHeight] = useState('');
  const [currentThickness, setCurrentThickness] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState('1');
  const [currentColor, setCurrentColor] = useState('');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [bypassDimensionLimits, setBypassDimensionLimits] = useState(false);

  // Load materials when dialog opens
  useEffect(() => {
    if (open && accessToken) {
      loadMaterials();
    }
  }, [open, accessToken]);

  const loadMaterials = async () => {
    if (!accessToken) return;

    try {
      setLoadingMaterials(true);
      const data = await materialsService.fetchMaterials(accessToken);
      setMaterials(data.filter(m => m.active && !m.deletedAt));
    } catch (err) {
      setError('Erro ao carregar materiais');
    } finally {
      setLoadingMaterials(false);
    }
  };

  // Get current dimension limits
  const currentDimensionLimits = useMemo(() => {
    const material = materials.find(m => m.id === currentMaterialId);
    const thickness = parseFloat(currentThickness);

    if (!material || !thickness || isNaN(thickness)) {
      return null;
    }

    const limits = getMaxDimensions(thickness, material.dimensionLimits);
    return {
      maxWidthCm: limits.maxWidth * 100,
      maxHeightCm: limits.maxHeight * 100
    };
  }, [currentMaterialId, currentThickness, materials]);

  // Auto-correct dimensions when thickness changes (skip if bypass enabled)
  useEffect(() => {
    if (!currentDimensionLimits || bypassDimensionLimits) {
      return;
    }

    const width = parseFloat(currentWidth);
    const height = parseFloat(currentHeight);

    if (!isNaN(width) && width > currentDimensionLimits.maxWidthCm) {
      setCurrentWidth(currentDimensionLimits.maxWidthCm.toFixed(1));
    }
    if (!isNaN(height) && height > currentDimensionLimits.maxHeightCm) {
      setCurrentHeight(currentDimensionLimits.maxHeightCm.toFixed(1));
    }
  }, [currentDimensionLimits, bypassDimensionLimits]);

  // Handle width change with auto-clamping (skip if bypass enabled)
  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value) {
      setCurrentWidth('');
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return;
    }

    if (!bypassDimensionLimits && currentDimensionLimits && numValue > currentDimensionLimits.maxWidthCm) {
      setCurrentWidth(currentDimensionLimits.maxWidthCm.toFixed(1));
    } else {
      setCurrentWidth(value);
    }
  };

  // Handle height change with auto-clamping (skip if bypass enabled)
  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value) {
      setCurrentHeight('');
      return;
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return;
    }

    if (!bypassDimensionLimits && currentDimensionLimits && numValue > currentDimensionLimits.maxHeightCm) {
      setCurrentHeight(currentDimensionLimits.maxHeightCm.toFixed(1));
    } else {
      setCurrentHeight(value);
    }
  };

  // Calculate price
  useEffect(() => {
    const material = materials.find(m => m.id === currentMaterialId);
    if (!material) {
      setCurrentPrice(0);
      return;
    }

    const width = parseFloat(currentWidth);
    const height = parseFloat(currentHeight);
    const thickness = parseFloat(currentThickness);
    const quantity = parseInt(currentQuantity, 10);

    if (isNaN(width) || isNaN(height) || isNaN(thickness) || isNaN(quantity)) {
      setCurrentPrice(0);
      return;
    }

    if (width <= 0 || height <= 0 || thickness <= 0 || quantity <= 0) {
      setCurrentPrice(0);
      return;
    }

    // Calculate: width(m) × height(m) × thickness(mm/1000) × density(g/cm³*1000 = kg/m³) × pricePerKg
    const widthMeters = width / 100; // convert cm to m
    const heightMeters = height / 100; // convert cm to m
    const thicknessMeters = thickness / 1000; // convert mm to m
    const volumeM3 = widthMeters * heightMeters * thicknessMeters;
    const densityKgM3 = material.density * 1000; // convert g/cm³ to kg/m³
    const weightKg = volumeM3 * densityKgM3;
    const unitPrice = weightKg * material.pricePerKg;
    const subtotal = unitPrice * quantity;

    setCurrentPrice(subtotal);
  }, [currentMaterialId, currentWidth, currentHeight, currentThickness, currentQuantity, materials]);

  const handleAddItem = () => {
    const material = materials.find(m => m.id === currentMaterialId);
    if (!material) return;

    const width = parseFloat(currentWidth);
    const height = parseFloat(currentHeight);
    const thickness = parseFloat(currentThickness);
    const quantity = parseInt(currentQuantity, 10);

    if (isNaN(width) || isNaN(height) || isNaN(thickness) || isNaN(quantity)) {
      setError('Preencha todos os campos do item');
      return;
    }

    if (width <= 0 || height <= 0 || thickness <= 0 || quantity <= 0) {
      setError('Todos os valores devem ser maiores que zero');
      return;
    }

    if (currentPrice <= 0) {
      setError('Preço calculado inválido');
      return;
    }

    const newItem: QuoteItemForm = {
      id: `item-${Date.now()}-${Math.random()}`,
      materialId: currentMaterialId,
      width: width / 100, // Convert cm to meters for API
      height: height / 100, // Convert cm to meters for API
      thickness,
      quantity,
      color: currentColor || undefined,
      unitPrice: currentPrice / quantity,
      subtotal: currentPrice
    };

    setItems([...items, newItem]);

    // Reset current item form
    setCurrentMaterialId('');
    setCurrentWidth('');
    setCurrentHeight('');
    setCurrentThickness('');
    setCurrentQuantity('1');
    setCurrentColor('');
    setCurrentPrice(0);
    setError(null);
  };

  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleAddCustomItem = () => {
    const value = parseFloat(currentCustomValue);

    if (!currentCustomDescription.trim()) {
      setError('Descrição do item personalizado é obrigatória');
      return;
    }

    if (isNaN(value) || value <= 0) {
      setError('Valor do item personalizado deve ser maior que zero');
      return;
    }

    const newItem: CustomItemForm = {
      id: `custom-${Date.now()}-${Math.random()}`,
      description: currentCustomDescription.trim(),
      value
    };

    setCustomItems([...customItems, newItem]);
    setCurrentCustomDescription('');
    setCurrentCustomValue('');
    setError(null);
  };

  const handleRemoveCustomItem = (id: string) => {
    setCustomItems(customItems.filter(item => item.id !== id));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!accessToken) {
      setError('Não autenticado');
      return;
    }

    if (items.length === 0 && customItems.length === 0) {
      setError('Adicione pelo menos um item (material ou personalizado)');
      return;
    }

    if (!customerName || !customerDocument || !customerEmail || !customerPhone) {
      setError('Preencha todos os campos obrigatórios do cliente');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload: quotesService.CreateQuotePayload = {
        customer: {
          name: customerName,
          company: customerCompany || undefined,
          document: customerDocument,
          email: customerEmail,
          phone: customerPhone
        },
        items: items.length > 0 ? items.map(item => ({
          materialId: item.materialId,
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          quantity: item.quantity,
          color: item.color
        })) : undefined,
        customItems: customItems.length > 0 ? customItems.map(item => ({
          description: item.description,
          value: item.value
        })) : undefined
      };

      await quotesService.createQuote(payload, accessToken);

      // Reset form
      setCustomerName('');
      setCustomerCompany('');
      setCustomerDocument('');
      setCustomerEmail('');
      setCustomerPhone('');
      setItems([]);
      setCustomItems([]);
      setCurrentMaterialId('');
      setCurrentWidth('');
      setCurrentHeight('');
      setCurrentThickness('');
      setCurrentQuantity('1');
      setCurrentColor('');
      setCurrentPrice(0);
      setCurrentCustomDescription('');
      setCurrentCustomValue('');

      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar orçamento');
    } finally {
      setSubmitting(false);
    }
  };

  const itemsTotal = items.reduce((sum, item) => sum + item.subtotal, 0);
  const customItemsTotal = customItems.reduce((sum, item) => sum + item.value, 0);
  const totalAmount = itemsTotal + customItemsTotal;
  const selectedMaterial = materials.find(m => m.id === currentMaterialId);
  const isColoridoMaterial = selectedMaterial?.name.toLowerCase().includes('colorido');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Novo Orçamento</DialogTitle>
          <DialogDescription>
            Preencha os dados do cliente e adicione os itens do orçamento
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold">Dados do Cliente</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="customerName">Nome Completo *</Label>
                <Input
                  id="customerName"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="João da Silva"
                  required
                />
              </div>
              <div>
                <Label htmlFor="customerCompany">Empresa</Label>
                <Input
                  id="customerCompany"
                  value={customerCompany}
                  onChange={(e) => setCustomerCompany(e.target.value)}
                  placeholder="Empresa Ltda (opcional)"
                />
              </div>
              <div>
                <Label htmlFor="customerDocument">CPF/CNPJ *</Label>
                <Input
                  id="customerDocument"
                  value={customerDocument}
                  onChange={(e) => documentMask.handleChange(e, setCustomerDocument)}
                  placeholder="000.000.000-00"
                  required
                />
              </div>
              <div>
                <Label htmlFor="customerEmail">Email *</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="joao@email.com"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="customerPhone">Telefone *</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => phoneMask.handleChange(e, setCustomerPhone)}
                  placeholder="(00) 00000-0000"
                  required
                />
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold">Itens do Orçamento</h3>

            {/* Add Item Form */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 border-b pb-4">
              <div className="md:col-span-2 lg:col-span-4">
                <Label htmlFor="material">Material *</Label>
                <select
                  id="material"
                  value={currentMaterialId}
                  onChange={(e) => setCurrentMaterialId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loadingMaterials}
                >
                  <option value="">Selecione o material</option>
                  {materials.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.code} - {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="thickness">Espessura (mm) *</Label>
                <Input
                  id="thickness"
                  type="number"
                  step="0.1"
                  min="0"
                  value={currentThickness}
                  onChange={(e) => setCurrentThickness(e.target.value)}
                  placeholder="3"
                />
              </div>

              <div>
                <Label htmlFor="quantity">Quantidade *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={currentQuantity}
                  onChange={(e) => setCurrentQuantity(e.target.value)}
                  placeholder="1"
                />
              </div>

              <div>
                <Label htmlFor="width">Largura (cm) *</Label>
                <Input
                  id="width"
                  type="number"
                  step="0.1"
                  min="0"
                  max={bypassDimensionLimits ? undefined : currentDimensionLimits?.maxWidthCm}
                  value={currentWidth}
                  onChange={handleWidthChange}
                  placeholder="100"
                />
                {currentDimensionLimits && !bypassDimensionLimits && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Máx: {currentDimensionLimits.maxWidthCm.toFixed(0)}cm
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="height">Altura (cm) *</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.1"
                  min="0"
                  max={bypassDimensionLimits ? undefined : currentDimensionLimits?.maxHeightCm}
                  value={currentHeight}
                  onChange={handleHeightChange}
                  placeholder="100"
                />
                {currentDimensionLimits && !bypassDimensionLimits && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Máx: {currentDimensionLimits.maxHeightCm.toFixed(0)}cm
                  </div>
                )}
              </div>

              {/* Bypass dimension limits checkbox - show when material is selected so user can enable before selecting thickness */}
              {currentMaterialId && (
                <div className="md:col-span-2 lg:col-span-4 flex items-center gap-2">
                  <Checkbox
                    id="bypass-limits"
                    checked={bypassDimensionLimits}
                    onCheckedChange={(checked) => setBypassDimensionLimits(checked === true)}
                  />
                  <Label htmlFor="bypass-limits" className="text-sm font-normal cursor-pointer">
                    Dimensões customizadas (ignorar limites)
                  </Label>
                </div>
              )}

              {isColoridoMaterial && (
                <div className="md:col-span-2 lg:col-span-4">
                  <Label htmlFor="color">Cor</Label>
                  <Input
                    id="color"
                    value={currentColor}
                    onChange={(e) => setCurrentColor(e.target.value)}
                    placeholder="Ex: Vermelho, Azul, Verde..."
                  />
                </div>
              )}

              <div className="md:col-span-2 lg:col-span-4 flex items-end justify-between gap-4">
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground">Subtotal</div>
                  <div className="text-lg font-semibold">
                    R$ {currentPrice.toFixed(2)}
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!currentMaterialId || currentPrice <= 0}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar
                </Button>
              </div>
            </div>

            {/* Items List */}
            {items.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Itens de Material ({items.length})</h4>
                <div className="space-y-2">
                  {items.map((item) => {
                    const material = materials.find(m => m.id === item.materialId);
                    return (
                      <div key={item.id} className="flex items-center justify-between gap-2 border rounded p-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{material?.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {(item.width * 100).toFixed(1)}cm × {(item.height * 100).toFixed(1)}cm × {item.thickness}mm
                            {' · Qtd: '}{item.quantity}
                            {item.color && ` · Cor: ${item.color}`}
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <div className="font-semibold">R$ {item.subtotal.toFixed(2)}</div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Custom Items Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold">Itens Personalizados</h3>
            <p className="text-sm text-muted-foreground">
              Adicione serviços extras, fretes ou outros itens que não são materiais calculados.
            </p>

            {/* Add Custom Item Form */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 border-b pb-4">
              <div className="md:col-span-2">
                <Label htmlFor="customDescription">Descrição *</Label>
                <Input
                  id="customDescription"
                  value={currentCustomDescription}
                  onChange={(e) => setCurrentCustomDescription(e.target.value)}
                  placeholder="Ex: Frete, Instalação, Corte especial..."
                />
              </div>

              <div>
                <Label htmlFor="customValue">Valor (R$) *</Label>
                <Input
                  id="customValue"
                  type="number"
                  step="0.01"
                  min="0"
                  value={currentCustomValue}
                  onChange={(e) => setCurrentCustomValue(e.target.value)}
                  placeholder="100.00"
                />
              </div>

              <div className="md:col-span-3 flex justify-end">
                <Button
                  type="button"
                  onClick={handleAddCustomItem}
                  disabled={!currentCustomDescription.trim() || !currentCustomValue}
                  variant="secondary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar Item Personalizado
                </Button>
              </div>
            </div>

            {/* Custom Items List */}
            {customItems.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Itens Personalizados Adicionados ({customItems.length})</h4>
                <div className="space-y-2">
                  {customItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-2 border border-gray-600 bg-gray-700 rounded p-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-100">{item.description}</div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div className="font-semibold text-emerald-400">R$ {item.value.toFixed(2)}</div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCustomItem(item.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Total Summary */}
          {(items.length > 0 || customItems.length > 0) && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <div className="space-y-2">
                {items.length > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Materiais:</span>
                    <span>R$ {itemsTotal.toFixed(2)}</span>
                  </div>
                )}
                {customItems.length > 0 && (
                  <div className="flex justify-between items-center text-sm text-blue-600">
                    <span>Itens Personalizados:</span>
                    <span>+R$ {customItemsTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="font-semibold">Total:</span>
                  <span className="text-lg font-bold">R$ {totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || (items.length === 0 && customItems.length === 0)}>
              {submitting ? 'Criando...' : 'Criar Orçamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
